/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import InputBox from "../../../components/InputBox";
import { Button } from "src/business-types/grocery/components/Button";
import iconMap from "../../../../../assets/icons/Icons";
import BarcodeComponent from "../../../../../shared/utils/generateBarcode/BarcodeComponent";
import { putProduct } from "../../../../../shared/services/services";
import { CategoryDetails, ProductItem } from "../../../../../shared/services/interface";
import { closeLoading, notifyError, notifyLoading, notifySuccess, notifyWarning } from "../../../../../assets/toastService";
import DropdownSelect from "../../../components/DropdownSelect";
import BarcodeScanner from "../../../../../shared/utils/scanningBarcode/BarcodeScanner";
import PopupModules from "src/features/auth/components/PopupModules";
import { ThemeBasedColorHeader, ThemeBasedColorMenubar } from "src/shared/modules/profile/theme/themeCheacker";
import { errorHandling } from "src/shared/services/errorHandling";
import useUOM from "src/shared/hooks/useUOM";
import MultiSelect from "src/business-types/grocery/components/MultiSelect";
import CustomImage from "src/shared/Image";

type Props = {
  editData: ProductItem;
  close: (data: any) => void;
  category: CategoryDetails[];
  loading: (state: boolean) => void;
};

type FormData = {
  ProductName: string;
  Amount: string;
  Type: string;
  ArabicName: string;
  own_product: boolean;
  category_id: string;
  categoryName: string;
  staff_list: string[];
  image: File | string | null;
  ImagePreview: string;
  barcodeId: string;
  uom: { _id: string; uomName: string }[];
  product_id: string | number;
  amountMeasurement: string;
  purchasePrice: number;
  sellingPrice: number;
  discount: number;
  discountType: boolean;
};

const EditProducts: React.FC<Props> = ({ editData, close, category, loading }) => {
  const [productType, setProductType] = useState<"Inward" | "Service">(editData.own_product ? "Inward" : "Service");
  const [formData, setFormData] = useState<FormData>({
    ProductName: editData.ProductName || "",
    Amount: editData.Amount || "",
    Type: editData.Type || "",
    ArabicName: editData.ArabicName || "",
    own_product: editData.own_product || false,
    category_id: editData.category_id || "",
    categoryName: editData.category_name || "",
    staff_list: editData.staff_list || [],
    image: editData.image_url || null,
    ImagePreview: editData.image_url || "",
    barcodeId: editData.barcodeId || "",
    uom: editData.uom || [],
    product_id: editData.product_id || "",
    amountMeasurement: editData.amountMeasurement || "",
    purchasePrice: Number(editData.purchasePrice) ?? 0,
    sellingPrice: Number(editData.sellingPrice) ?? 0,
    discount: Number(editData.discount) ?? 0,
    discountType: false,
  });

  useEffect(() => {
    if (editData?.discount) {
      const discountValue = String(editData.discount);
      const isPercentage = discountValue.includes("%");

      setFormData((prev) => ({
        ...prev,
        discountType: isPercentage,
        discount: Number(discountValue.split("%")[0] || discountValue),
      }));
    }
  }, [editData.discount]);

  const { uom } = useUOM();
  const [barcodeMode, setBarcodeMode] = useState(!!editData.barcodeId);
  const [scanMode, setScanMode] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [submit, setSubmit] = useState(false);
  const BarcodeIcon = iconMap["SharpIcon"];

  useEffect(() => {
    if (editData.uom[0] && typeof editData.uom[0] === "string") {
      console.log(editData.uom);
      const editUom = editData.uom.map((u) => uom.find((unit: any) => unit.uom_name === u)).filter((u) => u !== undefined) as {
        id: string;
        uom_name: string;
      }[];
      const updatedUOM = editUom.map((u) => ({ _id: u.id, uomName: u.uom_name }));
      console.log("object111", updatedUOM, editUom);
      setFormData((prev) => ({ ...prev, uom: updatedUOM, Amount: editData.Amount, amountMeasurement: editData.amountMeasurement }));
    }
  }, [uom]);

  // 🧠 Auto-fill category name when selected
  useEffect(() => {
    const selectedCategory = category.find((i) => i._id.$oid === formData.category_id);
    if (selectedCategory) {
      setFormData((prev) => ({ ...prev, categoryName: selectedCategory.CategoryName }));
    }
  }, [formData.category_id, category]);

  // 🖼️ Handle Image Compression (max 100KB)
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const img = new Image();
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === "string") img.src = reader.result;
    };

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const maxWidth = 150;
      const scale = maxWidth / img.width;
      canvas.width = maxWidth;
      canvas.height = img.height * scale;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        console.log(blob?.size);
        if (blob && blob.size <= 160 * 1024) {
          const compressed = new File([blob], "image.png", { type: "image/png" });
          setFormData((prev) => ({
            ...prev,
            image: compressed,
            ImagePreview: URL.createObjectURL(compressed),
          }));
        } else {
          notifyError("Image must be under 100KB");
        }
      }, "image/png");
    };

    reader.readAsDataURL(file);
  };

  // 🔍 Barcode Scanning
  const handleBarcodeDetected = (code: string) => {
    setFormData((prev) => ({ ...prev, barcodeId: code }));
    setScanMode(false);
    setBarcodeMode(true);
  };

  // 🚀 Update Product
  const handleSubmit = async () => {
    setSubmit(true);
    notifyLoading("Updating product...");
    loading(true);
    try {
      // Create FormData instance
      const payload = new FormData();

      // Append only changed fields
      // ✅ Helper: Convert any value to number safely
      const toNumber = (val: any): number => Number(val) || 0;

      // ✅ Normalize form values
      const sellingPrice = toNumber(formData.sellingPrice);
      const purchasePrice = toNumber(formData.purchasePrice);
      const discountValue = toNumber(discount);

      // ✅ Normalize edit values
      const editSellingPrice = toNumber(editData.sellingPrice);
      const editPurchasePrice = toNumber(editData.purchasePrice);
      const editDiscountValue = toNumber(discountEdit);

      // ✅ Helper: append only if changed
      const appendIfChanged = (key: string, newVal: any, oldVal: any) => {
        if (newVal !== oldVal) {
          payload.append(key, String(newVal));
        }
      };

      // ✅ Normal fields
      if (formData.ProductName !== editData.ProductName) payload.append("ProductName", formData.ProductName);

      if (formData.ArabicName !== editData.ArabicName) payload.append("ArabicName", formData.ArabicName);

      if (formData.Type !== editData.Type) payload.append("Type", formData.Type);

      if (formData.own_product !== editData.own_product) payload.append("own_product", String(formData.own_product));

      if (formData.category_id !== editData.category_id) payload.append("category_id", formData.category_id);

      if (formData.categoryName !== editData.category_name) payload.append("categoryName", formData.categoryName);

      if (formData.product_id !== editData.product_id) payload.append("product_id", String(formData.product_id));

      if (formData.barcodeId !== editData.barcodeId) payload.append("barcodeId", formData.barcodeId);

      // ✅ Array compare
      if (JSON.stringify(formData.uom) !== JSON.stringify(editData.uom)) {
        payload.append("uom", JSON.stringify(formData.uom));
      }

      if (formData.amountMeasurement !== editData.amountMeasurement) {
        payload.append("amountMeasurement", formData.amountMeasurement);
      }

      // ✅ Price & Discount (Fixed)
      appendIfChanged("sellingPrice", sellingPrice, editSellingPrice);
      appendIfChanged("purchasePrice", purchasePrice, editPurchasePrice);
      appendIfChanged("discount", discountValue, editDiscountValue);

      if (formData.image instanceof File) {
        payload.append("image_url", formData.image);
      }

      console.log(payload.entries().next());

      // If nothing changed
      if (payload.entries().next().done) {
        notifyError("No changes detected");
        return;
      }

      await putProduct(editData._id.$oid, payload);
      notifySuccess("Product updated successfully");
      close("");
    } catch (err) {
      errorHandling(err);
    } finally {
      closeLoading();
      setSubmit(false);
      loading(false);
    }
  };

  const checkMandatoryFields = () => {
    if (!formData.ProductName || !formData.category_id) {
      return false;
    }

    if (productType === "Inward") {
      // For Inward products with UOM enabled
      if (formData.uom.length > 0) {
        return !!formData.amountMeasurement;
      }
    } else {
      // For Service products
      return !!formData.Amount;
    }
  };
  const discount = formData.discount + (formData.discountType === true ? "%" : "");
  const discountEdit = editData.discount + (formData.discountType === true ? "%" : "");

  const hasChanges = () => {
    // Check all fields for changes
    if (formData.ProductName !== editData.ProductName) return true;
    if (formData.Amount !== editData.Amount) return true;
    if (formData.ArabicName !== editData.ArabicName) return true;
    if (formData.Type !== (editData.Type === null ? "" : editData.Type)) return true;
    if (productType !== (editData.own_product === true ? "Inward" : "Service")) return true;
    if (formData.category_id !== editData.category_id) return true;
    if (formData.categoryName !== editData.category_name) return true;
    if (formData.product_id !== editData.product_id) return true;
    if (formData.barcodeId !== editData.barcodeId) return true;
    if (JSON.stringify(formData.uom) !== JSON.stringify(editData.uom)) return true;
    if (formData.amountMeasurement !== editData.amountMeasurement) return true;
    if (String(formData.sellingPrice) !== editData.sellingPrice) return true;
    if (String(formData.purchasePrice) !== editData.purchasePrice) return true;
    if (discount !== discountEdit) return true;

    // Check if image was changed (new File uploaded)
    if (formData.image instanceof File) return true;
    return false;
  };
  const canSubmit = checkMandatoryFields() && hasChanges() && !submit;

  console.log(canSubmit, checkMandatoryFields(), hasChanges());

  const renderInput = (
    label: string,
    name: keyof FormData,
    type = "text",
    mandatory: boolean = false,
    onlyNumber: boolean = false,
    disabled: boolean = false,
  ) => (
    <div className='mb-3' id={label}>
      <label htmlFor={name} className='flex'>
        <p className='text-sm font-medium mb-1'>{label}</p>
        {mandatory && <p className='text-red-600'>*</p>}
      </label>
      <InputBox
        id={name}
        type={type}
        value={formData[name] as string}
        onChange={(e) => setFormData({ ...formData, [name]: e.target.value })}
        onKeyDown={(e) => {
          if (type === "number" && ["-", "e", "E"].includes(e.key)) e.preventDefault();
        }}
        maxLength={40}
        min={type === "number" ? 0.01 : undefined}
        onlyNumber={onlyNumber}
        disabled={disabled}
        placeholder={name === "sellingPrice" ? "0" : label}
      />
    </div>
  );

  useEffect(() => {
    if (formData.uom.length === 0) {
      setFormData((prev) => ({ ...prev, Amount: "", amountMesurment: "" }));
    }
  }, [formData.uom]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const isPercentage = formData.discountType === true;
      const discountAmount = Number(formData.discount);
      const sellingPrice = Number(formData.sellingPrice);
      const purchasePrice = Number(formData.purchasePrice);

      // % discount cannot exceed 99
      if (isPercentage && discountAmount > 99) {
        notifyWarning("Percentage discount cannot be more than 99%");
        setFormData((prev) => ({ ...prev, discount: 0 }));
        return;
      }

      if (Number(sellingPrice) === Number(editData.sellingPrice) && Number(purchasePrice) === Number(editData.purchasePrice)) {
        return;
      }

      if (sellingPrice === 0 && purchasePrice === 0) {
        return;
      }

      // Flat discount cannot be >= selling price
      if (!isPercentage && discountAmount >= sellingPrice) {
        notifyWarning("Discount amount must be less than the selling price");
        return;
      }

      // Discounted price must be > purchase price
      if (!isPercentage && purchasePrice >= sellingPrice - discountAmount) {
        notifyWarning("Discount price must be greater than the purchase price");
        return;
      }
    }, 500); // 👈 delay time in milliseconds

    return () => clearTimeout(timer); // cleanup
  }, [formData.discount, formData.discountType, formData.sellingPrice, formData.purchasePrice]);

  const sellingPrice = Number(formData.sellingPrice) || 0;
  const discounts = Number(formData.discount) || 0;
  const purchasePrice = Number(formData.purchasePrice);

  const discountPrice = formData.discountType
    ? (sellingPrice * discounts) / 100 // Percentage discount
    : discounts;

  const finalPrice = sellingPrice - discountPrice;
  return (
    <PopupModules isOpen={!!editData} onClose={() => close("")} title='Edit Product'>
      <div className='w-full max-w-md space-y-3'>
        {/* Toggle Product Type */}
        <div className='flex gap-2 mb-3'>
          {["Inward", "Service"].map((type) => (
            <button
              key={type}
              onClick={() => {
                setProductType(type as "Inward" | "Service");
                setFormData((prev) => ({ ...prev, own_product: type === "Inward" }));
              }}
              className={`flex-1 py-1.5 rounded-md text-sm font-medium ${productType === type ? ThemeBasedColorHeader() : ThemeBasedColorMenubar()}`}
            >
              {type} Products
            </button>
          ))}
        </div>

        {/* Product Form */}
        <div className={`border border-gray-300 rounded-xl p-3 bg-gray-50 shadow-inner space-y-2`}>
          <div className='flex gap-2'>
            <div className='w-3/5'>
              {renderInput("Product Name", "ProductName", "text", true)}
              {renderInput("Second Language Name", "ArabicName")}
              <DropdownSelect
                title='Category Type'
                name='category_id'
                value={formData.category_id}
                options={category.map((c) => ({ _id: c._id.$oid, CategoryName: c.CategoryName }))}
                onChange={(e) => setFormData({ ...formData, category_id: e })}
                mandatory={true}
              />
            </div>

            <div className='w-2/5 flex flex-col items-center'>
              <div
                onClick={() => fileRef.current?.click()}
                className='cursor-pointer border border-gray-300 w-[139px] h-[139px] rounded-lg overflow-hidden flex items-center justify-center bg-white'
              >
                {formData.ImagePreview ? (
                  editData.image_url === formData.ImagePreview ? (
                    <CustomImage imagePath={formData.ImagePreview} />
                  ) : (
                    <img src={formData.ImagePreview} alt='productImage' className='w-full h-full object-cover' />
                  )
                ) : (
                  <span className='text-xs text-gray-400 text-center px-2'>Upload Image (under 100KB)</span>
                )}
                <input ref={fileRef} type='file' accept='image/*' onChange={handleImageChange} className='hidden' />
              </div>
              {renderInput("Product ID", "product_id")}
            </div>
          </div>

          {productType !== "Inward" && renderInput("Amount", "Amount", "text", true)}
          {productType === "Inward" && (
            <>
              <div className='flex w-full'>
                <MultiSelect
                  title='Unit Of Measurement'
                  name='uom'
                  value={formData.uom.map((u) => u.uomName)}
                  options={uom.map((u) => ({ _id: u.uom_name, CategoryName: u.uom_name }))}
                  onChange={(selected) =>
                    setFormData({ ...formData, uom: selected.map((s) => ({ _id: uom.filter((i) => i.uom_name === s)[0]?.id || s, uomName: s })) })
                  }
                />
              </div>
              <div className='flex gap-2 w-full justify-between items-center '>
                <div className='w-1/2'>
                  <DropdownSelect
                    title='Amount of UOM'
                    name='uomBaseUnit'
                    value={formData.amountMeasurement}
                    options={formData.uom.map((c) => ({
                      _id: c._id,
                      CategoryName: uom.find((u) => u.uom_name === c.uomName)?.uom_name || c.uomName,
                    }))}
                    onChange={(e) => {
                      setFormData({ ...formData, amountMeasurement: e });
                      console.log(e);
                    }}
                    mandatory={true}
                    defultSelect={true}
                  />
                </div>
                {renderInput("Purchase Price", "purchasePrice", "text", false, true)}
              </div>
              <div className='flex gap-1 items-center'>
                {renderInput("Selling Price", "sellingPrice", "text", false, true, formData.uom.length === 0)}

                <div className='flex flex-col gap-1 items-end w-1/2'>
                  <div className='flex flex-col justify-end items-end -mb-7 w-full gap-1 mr-4'>
                    <div className='flex gap-2 justify-center items-center'>
                      <p className='select-none'> in %</p>
                      <input
                        type='checkbox'
                        checked={formData.discountType}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            discountType: e.target.checked,
                          }))
                        }
                        className='cursor-pointer scale-125'
                      />
                    </div>
                  </div>
                  <div className='w-full'>{renderInput("Discount", "discount", "text", false, true)}</div>
                </div>
              </div>

              <div className='w-full flex justify-between items-center gap-2'>
                <div className={`flex w-1/2`}>
                  {" "}
                  <p>Discount Value :</p>
                  <p> {discountPrice.toFixed(2)}</p>
                </div>
                <div
                  className={`flex w-1/2 ${
                    finalPrice < purchasePrice ? "text-red-500" : purchasePrice === finalPrice ? "text-orange-400" : "text-green-600"
                  }`}
                >
                  {" "}
                  <p>Final Price :</p>
                  <p> {finalPrice.toFixed(2)}</p>
                </div>
              </div>

              {purchasePrice > sellingPrice && (
                <p className='text-red-600 text-sm font-medium flex justify-center items-center'>
                  ⚠️ Are you sure the selling price <br />
                  is less than the purchase price?
                </p>
              )}

              <div className='text-center'>
                <div
                  onClick={() => setScanMode((prev) => !prev)}
                  className='flex justify-center items-center gap-2 bg-gray-200 px-3 py-1 rounded-md cursor-pointer'
                >
                  <BarcodeIcon /> <span>Add or Create Barcode</span>
                </div>

                {barcodeMode && (
                  <div className='my-2 flex justify-center'>
                    <BarcodeComponent value={formData.barcodeId} width={1.5} height={20} />
                  </div>
                )}

                {scanMode && <BarcodeScanner onDetected={(code: string) => handleBarcodeDetected(code)} />}
              </div>
            </>
          )}
        </div>

        {/* Buttons */}
        <div className='flex justify-center gap-3 mt-3'>
          <Button label='Cancel' variant='ThemeCancel' className='border' onClick={() => close("")} disabled={submit} />
          <Button label={submit ? "Loading ...." : "Confirm"} variant='ThemeBase' onClick={handleSubmit} disabled={!canSubmit} />
        </div>
      </div>
    </PopupModules>
  );
};

export default EditProducts;
